class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-short', FacetShort, { extends: 'select' });

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 200,
        behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
      });
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url, event) : this.renderSectionFromFetch(url, event);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    this.beforeRenderSection();
    const start = performance.now();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            this.renderFilters(responseText, event);
            this.renderFiltersActive(responseText);
            this.renderProductGridContainer(responseText);
            this.renderProductCount(responseText);
            this.renderSortBy(responseText);
            this.renderSortByMobile(responseText);

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
            this.cachedMap.set(url, responseText);

            this.afterRenderSection();
          },
          execution > 250 ? 0 : 250
        );
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter], .collection__filters-horizontal [data-filter]'
    );

    const facetElementsFormDom = document.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter], .collection__filters-horizontal [data-filter]'
    );

    Array.from(facetElementsFormDom).forEach((currentElement) => {
      if (!Array.from(facetElements).some(({ id }) => currentElement.id === id)) {
        currentElement.classList.add('hidden');
      }
    });

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        filter.classList.remove('hidden');
        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
    
    // Re-initialize horizontal facets after update
    if (window.horizontalFacets) {
      window.horizontalFacets.rebind();
    }
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    const layoutSwitcher = document.querySelector('layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

// Horizontal Facets Management - Works with existing AccordionDetails system
class HorizontalFacets {
  constructor() {
    this.currentOpenDropdown = null;
    this.init();
  }

  init() {
    this.bindEvents();
    this.bindShowMoreButtons();
    this.bindInStockCheckbox();
    this.bindSearchFields();
    this.customizeAccordionBehavior();
  }

  customizeAccordionBehavior() {
    // Override the accordion behavior for horizontal facets
    const horizontalAccordions = document.querySelectorAll('.facet-horizontal-item[is="accordion-details"]');
    
    horizontalAccordions.forEach(accordion => {
      // Remove the existing click listener
      const summary = accordion.querySelector('summary');
      if (summary) {
        // Override the handleAfterToggle method to implement our "only one open" logic
        const originalHandleAfterToggle = accordion.handleAfterToggle;
        accordion.handleAfterToggle = () => {
          if (accordion.isOpen) {
            // Close other horizontal dropdowns
            this.closeOtherDropdowns(accordion);
            this.currentOpenDropdown = accordion;
          } else if (this.currentOpenDropdown === accordion) {
            this.currentOpenDropdown = null;
          }
          
          // Call original method if it exists
          if (originalHandleAfterToggle) {
            originalHandleAfterToggle.call(accordion);
          }
        };
      }
    });
  }

  bindEvents() {
    // Handle clicks outside to close dropdowns
    document.addEventListener('click', (event) => {
      const isClickInside = event.target.closest('.facets-horizontal');
      const isClickOnSummary = event.target.closest('.facet-horizontal-item__summary');
      
      if (!isClickInside && !isClickOnSummary) {
        this.closeAllDropdowns();
      }
    });
  }

  closeOtherDropdowns(exceptDropdown) {
    const allHorizontalDropdowns = document.querySelectorAll('.facet-horizontal-item[is="accordion-details"]');
    
    allHorizontalDropdowns.forEach(dropdown => {
      if (dropdown !== exceptDropdown && dropdown.open) {
        dropdown.open = false;
      }
    });
  }

  closeAllDropdowns() {
    const allHorizontalDropdowns = document.querySelectorAll('.facet-horizontal-item[is="accordion-details"]');
    
    allHorizontalDropdowns.forEach(dropdown => {
      if (dropdown.open) {
        dropdown.open = false;
      }
    });
    
    this.currentOpenDropdown = null;
  }

  bindShowMoreButtons() {
    const showMoreButtons = document.querySelectorAll('.facet-show-more-btn:not([data-bound])');
    showMoreButtons.forEach(button => {
      button.setAttribute('data-bound', 'true');
      
      // Ensure initial state is correct
      this.initializeButtonState(button);
      
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        const showMoreLabel = button.querySelector('.label-show-more');
        const showLessLabel = button.querySelector('.label-show-less');
        
        this.toggleShowMore(button);
      });
    });
  }
  
  initializeButtonState(button) {
    const dropdown = button.closest('.facet-horizontal-item__dropdown');
    const showMoreLabel = button.querySelector('.label-show-more');
    const showLessLabel = button.querySelector('.label-show-less');
    
    // Initially, show more should be visible and show less should be hidden
    if (showMoreLabel) showMoreLabel.classList.remove('hidden');
    if (showLessLabel) showLessLabel.classList.add('hidden');
    
    // Update the count
    const hiddenItems = dropdown.querySelectorAll('.show-more-item.hidden:not(.search-hidden)').length;
    if (showMoreLabel && hiddenItems > 0) {
      showMoreLabel.textContent = `+ altri ${hiddenItems}`;
    }
  }

  toggleShowMore(button) {
    const dropdown = button.closest('.facet-horizontal-item__dropdown');
    const allShowMoreItems = dropdown.querySelectorAll('.show-more-item');
    const showMoreLabel = button.querySelector('.label-show-more');
    const showLessLabel = button.querySelector('.label-show-less');
    
    // Check current state more reliably
    const isCurrentlyShowingLess = showLessLabel && !showLessLabel.classList.contains('hidden');
    
    if (isCurrentlyShowingLess) {
      // User clicked "show less" - collapse to first 5 items
      allShowMoreItems.forEach((item) => {
        if (!item.classList.contains('search-hidden')) {
          item.classList.add('hidden');
        }
      });
      
      // Switch to show more state
      showLessLabel.classList.add('hidden');
      showMoreLabel.classList.remove('hidden');
      
      // Calculate and update count
      const hiddenItems = dropdown.querySelectorAll('.show-more-item.hidden:not(.search-hidden)').length;
      showMoreLabel.textContent = `+ altri ${hiddenItems}`;
      
    } else {
      // User clicked "show more" - show all remaining items
      const currentlyHidden = dropdown.querySelectorAll('.show-more-item.hidden:not(.search-hidden)');
      
      if (currentlyHidden.length > 0) {
        currentlyHidden.forEach(item => {
          item.classList.remove('hidden');
        });
        
        // Since showing all, switch to show less button
        showMoreLabel.classList.add('hidden');
        showLessLabel.classList.remove('hidden');
      }
    }
  }
  
  bindInStockCheckbox() {
    const inStockCheckbox = document.querySelector('.facet-instock-checkbox:not([data-bound])');
    if (inStockCheckbox) {
      inStockCheckbox.setAttribute('data-bound', 'true');
      inStockCheckbox.addEventListener('change', (event) => {
        const form = event.target.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');
        if (form) {
          // Trigger form change to update the collection
          form.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  bindSearchFields() {
    const searchInputs = document.querySelectorAll('.facet-search-input:not([data-bound])');
    searchInputs.forEach(input => {
      input.setAttribute('data-bound', 'true');
      input.addEventListener('input', (event) => {
        this.filterFacetItems(event.target);
      });
    });
  }

  filterFacetItems(searchInput) {
    const dropdown = searchInput.closest('.facet-horizontal-item__dropdown');
    const searchTerm = searchInput.value.toLowerCase().trim();
    const facetItems = dropdown.querySelectorAll('.facet-list-item, .facet-swatch-item, .facet-image-item');
    const showMoreButton = dropdown.querySelector('.facet-show-more-btn');
    const limit = 5;
    
    if (searchTerm === '') {
      // Reset to original state when search is cleared
      facetItems.forEach((item, index) => {
        item.classList.remove('search-hidden');
        
        // Reset to show first 5 items
        if (item.classList.contains('show-more-item')) {
          if (index >= limit) {
            item.classList.add('hidden');
          } else {
            item.classList.remove('hidden');
          }
        }
      });
      
      // Reset show more button
      if (showMoreButton) {
        const showMoreLabel = showMoreButton.querySelector('.label-show-more');
        const showLessLabel = showMoreButton.querySelector('.label-show-less');
        const hiddenItems = dropdown.querySelectorAll('.show-more-item.hidden:not(.search-hidden)').length;
        
        showMoreButton.style.display = 'block';
        showMoreLabel.classList.remove('hidden');
        showLessLabel.classList.add('hidden');
        showMoreLabel.textContent = `+ altri ${hiddenItems}`;
      }
    } else {
      // Apply search filter
      let visibleCount = 0;
      
      facetItems.forEach((item) => {
        const label = item.querySelector('label span:first-child, .facet-image-item__info span:first-child');
        const itemText = label ? label.textContent.toLowerCase() : '';
        
        if (itemText.includes(searchTerm)) {
          // Item matches search
          item.classList.remove('search-hidden');
          item.classList.remove('hidden'); // Show all matching items during search
          visibleCount++;
        } else {
          // Item doesn't match search
          item.classList.add('search-hidden');
        }
      });
      
      // Hide show more button during search
      if (showMoreButton) {
        showMoreButton.style.display = 'none';
      }
    }
  }

  // Re-bind events after AJAX updates
  rebind() {
    this.bindShowMoreButtons();
    this.bindInStockCheckbox();
    this.bindSearchFields();
    this.customizeAccordionBehavior();
    // Reset current dropdown reference
    this.currentOpenDropdown = null;
  }
}

// Initialize horizontal facets
function initializeHorizontalFacets() {
  if (!window.horizontalFacets) {
    window.horizontalFacets = new HorizontalFacets();
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeHorizontalFacets);
} else {
  // DOM is already loaded
  initializeHorizontalFacets();
}

// Re-initialize after facet updates
document.addEventListener('collection:rerendered', () => {
  if (window.horizontalFacets) {
    window.horizontalFacets.rebind();
  } else {
    initializeHorizontalFacets();
  }
});

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(evt) {
    evt.preventDefault();

    if (this.facetEl.classList.contains('xl:block')) {
      this.facetEl.classList.remove('xl:block');
    } else {
      this.facetEl.classList.add('xl:block');
    }
  }
}
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}
customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });
    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);